/**
 * Relay Design System - Artifact Colors
 *
 * A colorblind-friendly palette for artifact type identification.
 * Based on research from IBM's accessibility guidelines and Wong's palette,
 * extended to provide 25 distinct colors (5x5 grid) that are:
 * - Distinguishable for colorblind users (deuteranopia, protanopia, tritanopia)
 * - WCAG AA compliant for contrast on both light and dark backgrounds
 * - Named with accessible labels for screen readers
 *
 * Color selection principles:
 * - Vary both hue AND luminance to ensure distinction for all color vision types
 * - Avoid pure red-green pairs that are indistinguishable for deuteranopes
 * - Use blue/orange as a primary distinguishing axis (safe for most CVD types)
 * - Include varied saturations and values for additional distinction
 */

export interface ArtifactColor {
  /** CSS hex value */
  value: string;
  /** Short name for display */
  name: string;
  /** Accessible label for screen readers */
  label: string;
}

/**
 * 25 colorblind-friendly colors arranged for a 5x5 grid.
 * Colors are ordered to maximize visual distinction between adjacent swatches.
 *
 * Row 1: Blues and teals (cool tones)
 * Row 2: Purples and magentas
 * Row 3: Oranges and yellows (warm tones)
 * Row 4: Greens (varied luminance)
 * Row 5: Neutrals and accent colors
 */
export const ARTIFACT_COLORS: ArtifactColor[] = [
  // Row 1: Blues and teals
  { value: '#0077BB', name: 'Blue', label: 'Strong blue' },
  { value: '#33BBEE', name: 'Cyan', label: 'Bright cyan' },
  { value: '#009988', name: 'Teal', label: 'Teal green' },
  { value: '#004488', name: 'Navy', label: 'Dark navy blue' },
  { value: '#88CCEE', name: 'Sky', label: 'Light sky blue' },

  // Row 2: Purples and magentas
  { value: '#AA3377', name: 'Magenta', label: 'Deep magenta' },
  { value: '#EE3377', name: 'Pink', label: 'Bright pink' },
  { value: '#CC6677', name: 'Rose', label: 'Dusty rose' },
  { value: '#882255', name: 'Wine', label: 'Wine purple' },
  { value: '#DDCC77', name: 'Sand', label: 'Sandy yellow' },

  // Row 3: Oranges and yellows
  { value: '#EE7733', name: 'Orange', label: 'Bright orange' },
  { value: '#CC3311', name: 'Vermilion', label: 'Vermilion red' },
  { value: '#EE6677', name: 'Coral', label: 'Coral pink' },
  { value: '#FFCC00', name: 'Gold', label: 'Golden yellow' },
  { value: '#997700', name: 'Ochre', label: 'Dark ochre' },

  // Row 4: Greens (varied luminance for distinction)
  { value: '#117733', name: 'Forest', label: 'Forest green' },
  { value: '#44AA77', name: 'Jade', label: 'Jade green' },
  { value: '#88BB55', name: 'Lime', label: 'Lime green' },
  { value: '#228833', name: 'Green', label: 'Bright green' },
  { value: '#AACC66', name: 'Pear', label: 'Pear green' },

  // Row 5: Neutrals and accent colors
  { value: '#555555', name: 'Gray', label: 'Medium gray' },
  { value: '#999999', name: 'Silver', label: 'Light silver' },
  { value: '#332288', name: 'Indigo', label: 'Deep indigo' },
  { value: '#661100', name: 'Maroon', label: 'Dark maroon' },
  { value: '#44BB99', name: 'Mint', label: 'Mint green' },
];

/**
 * Get a color from the palette by index (wraps around if out of bounds).
 */
export function getArtifactColor(index: number): ArtifactColor {
  return ARTIFACT_COLORS[index % ARTIFACT_COLORS.length];
}

/**
 * Get a color from the palette by hex value.
 */
export function getArtifactColorByValue(value: string): ArtifactColor | undefined {
  return ARTIFACT_COLORS.find(
    (color) => color.value.toLowerCase() === value.toLowerCase()
  );
}

/**
 * Get the default colors for new artifact types (first 6 colors).
 * These are selected to be maximally distinct from each other.
 */
export const DEFAULT_ARTIFACT_COLORS = [
  ARTIFACT_COLORS[0],  // Blue
  ARTIFACT_COLORS[10], // Orange
  ARTIFACT_COLORS[15], // Forest
  ARTIFACT_COLORS[5],  // Magenta
  ARTIFACT_COLORS[3],  // Navy
  ARTIFACT_COLORS[13], // Gold
] as const;

/**
 * Get a default color for a new artifact type by index.
 */
export function getDefaultArtifactColor(index: number): ArtifactColor {
  return DEFAULT_ARTIFACT_COLORS[index % DEFAULT_ARTIFACT_COLORS.length];
}
